import React, { useState, useEffect } from "react";
// Import the API functions for PDF operations
import { getPdfById, updatePdf } from "../services/auth"; // Assuming api.js is in the same directory

const UploadPdfs = () => {
  // Initial structure for PDF uploads, including IDs that map to backend records.
  // The fileName will be populated from the backend on initial load.
  const initialPdfsData = [
    {
      id: 1,
      title: "દાદા નો ઇતિહાસ", // Grandfather's History
      file: null,
      status: { type: "", message: "" },
      loading: false,
      fileName: "", // This will be populated from backend's filepath
      fullPath: "", // This will be the full URL to the PDF
    },
    {
      id: 2,
      title: "પરિવાર વિશે", // About Family
      file: null,
      status: { type: "", message: "" },
      loading: false,
      fileName: "",
      fullPath: "",
    },
    {
      id: 3,
      title: "નોટબુક દાતાશ્રી", // Notebook Donors
      file: null,
      status: { type: "", message: "" },
      loading: false,
      fileName: "",
      fullPath: "",
    },
    {
      id: 4,
      title: "તેજસ્વી તારલાઓ", // Bright Stars
      file: null,
      status: { type: "", message: "" },
      loading: false,
      fileName: "",
      fullPath: "",
    },
    {
      id: 5,
      title: "પદવી ધારકો", // Degree Holders
      file: null,
      status: { type: "", message: "" },
      loading: false,
      fileName: "",
      fullPath: "",
    },
    {
      id: 6,
      title: "ઈનામ ના દાતાશ્રી", // Degree Holders
      file: null,
      status: { type: "", message: "" },
      loading: false,
      fileName: "",
      fullPath: "",
    },
    {
      id: 7,
      title: "હનુમાન જન્મોત્સવ હિસાબ કિતાબ", // Degree Holders
      file: null,
      status: { type: "", message: "" },
      loading: false,
      fileName: "",
      fullPath: "",
    },
    {
      id: 8,
      title: "તરસરાધામ મંદિર", // Degree Holders
      file: null,
      status: { type: "", message: "" },
      loading: false,
      fileName: "",
      fullPath: "",
    },
    {
      id: 9,
      title: "લવાજમ હિસાબ કિતાબ", // Degree Holders
      file: null,
      status: { type: "", message: "" },
      loading: false,
      fileName: "",
      fullPath: "",
    },
  ];

  const [pdfs, setPdfs] = useState(initialPdfsData);
  const [loadingInitialPdfs, setLoadingInitialPdfs] = useState(true);

  // Effect to fetch existing PDF details when the component mounts
  useEffect(() => {
    const fetchPdfs = async () => {
      const updatedPdfs = [...pdfs];
      for (let i = 0; i < updatedPdfs.length; i++) {
        try {
          const response = await getPdfById(updatedPdfs[i].id);
          if (response.success && response.data) {
            updatedPdfs[i].fileName = response.data.filepath; // Set filename from backend
            updatedPdfs[i].fullPath = response.data.fullPath; // Set full URL
          } else {
            // If PDF not found, set a message
            updatedPdfs[i].status = {
              type: "info",
              message: `PDF for "${updatedPdfs[i].title}" not found.`,
            };
          }
        } catch (error) {
          console.error(
            `Error fetching PDF for ID ${updatedPdfs[i].id}:`,
            error
          );
          updatedPdfs[i].status = {
            type: "error",
            message: `Failed to load existing PDF for "${updatedPdfs[i].title}".`,
          };
        }
      }
      setPdfs(updatedPdfs);
      setLoadingInitialPdfs(false);
    };

    fetchPdfs();
  }, []); // Run only once on component mount

  // Effect to clear status messages after a delay
  useEffect(() => {
    pdfs.forEach((pdf, index) => {
      if (pdf.status.message) {
        const timer = setTimeout(() => {
          const newPdfs = [...pdfs];
          newPdfs[index].status = { type: "", message: "" };
          setPdfs(newPdfs);
        }, 5000); // Display status for 5 seconds

        return () => clearTimeout(timer);
      }
    });
  }, [pdfs]); // Rerun effect if pdfs array (or its contents) changes

  const handleFileSelect = (index, event) => {
    const newPdfs = [...pdfs];
    const selectedFile = event.target.files[0];
    if (selectedFile) {
      newPdfs[index].file = selectedFile;
      newPdfs[index].fileName = selectedFile.name; // Display selected file name
      newPdfs[index].status = { type: "", message: "" }; // Clear previous status
    } else {
      newPdfs[index].file = null;
      newPdfs[index].fileName = "";
    }
    setPdfs(newPdfs);
  };

  const handleUpdate = async (index) => {
    const newPdfs = [...pdfs];
    const currentPdf = newPdfs[index];

    if (!currentPdf.file) {
      currentPdf.status = {
        type: "error",
        message: `${currentPdf.title}: Please select a PDF file to update.`,
      };
      setPdfs(newPdfs);
      return;
    }

    if (currentPdf.file.type !== "application/pdf") {
      currentPdf.status = {
        type: "error",
        message: `${currentPdf.title}: Invalid file type. Please upload a PDF.`,
      };
      setPdfs(newPdfs);
      return;
    }

    currentPdf.loading = true;
    currentPdf.status = { type: "", message: "" }; // Clear previous status before saving
    setPdfs([...newPdfs]); // Update loading state immediately

    try {
      const formData = new FormData();
      formData.append("pdfFile", currentPdf.file); // 'pdfFile' must match the name expected by Multer in your backend

      const response = await updatePdf(currentPdf.id, formData);

      if (response.success) {
        currentPdf.status = { type: "success", message: response.message };
        // Update the displayed filename and fullPath from the backend response
        currentPdf.fileName = response.data.filepath;
        currentPdf.fullPath = response.data.filepath; // Assuming backend returns fullPath or reconstruct it
        currentPdf.file = null; // Clear the selected file after successful upload
      } else {
        currentPdf.status = {
          type: "error",
          message:
            response.message || `${currentPdf.title}: Failed to update PDF.`,
        };
      }
    } catch (error) {
      currentPdf.status = {
        type: "error",
        message:
          error.response?.data?.message ||
          error.message ||
          `${currentPdf.title}: Failed to update PDF.`,
      };
      console.error(`Update error for ${currentPdf.title}:`, error);
    } finally {
      currentPdf.loading = false;
      setPdfs([...newPdfs]); // Update state after attempt
    }
  };

  if (loadingInitialPdfs) {
    return (
      <div className="flex justify-center items-center h-screen text-xl text-indigo-700">
        Loading PDFs...
      </div>
    );
  }

  return (
    <div className="p-8 max-w-3xl mx-auto">
      <h1 className="text-3xl font-bold mb-8 text-center text-indigo-700">
        તમારા પીડીએફ દસ્તાવેજો અપલોડ કરો
      </h1>

      {pdfs.map((pdf, index) => (
        <div
          key={pdf.id}
          className="mb-10 p-6 border border-gray-300 rounded-lg shadow-md bg-white"
        >
          <h2 className="text-xl font-semibold mb-3 text-gray-700">
            {pdf.title}
          </h2>

          {pdf.status.message && (
            <div
              className={`mb-4 p-3 rounded-md text-sm ${
                pdf.status.type === "success"
                  ? "bg-green-100 text-green-700 border border-green-200"
                  : pdf.status.type === "info"
                  ? "bg-blue-100 text-blue-700 border border-blue-200"
                  : "bg-red-100 text-red-700 border border-red-200"
              }`}
            >
              {pdf.status.message}
            </div>
          )}

          <div className="flex flex-col sm:flex-row items-center space-y-3 sm:space-y-0 sm:space-x-4">
            <div className="relative w-full sm:w-auto">
              {" "}
              {/* Adjusted for better flex behavior */}
              <label
                htmlFor={`file-upload-${pdf.id}`}
                className={`w-full cursor-pointer inline-flex items-center justify-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500`}
              >
                {pdf.fileName ? (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5 mr-2 text-green-500"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                      clipRule="evenodd"
                    />
                  </svg>
                ) : (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5 mr-2 text-gray-400"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"
                    />
                  </svg>
                )}
                {pdf.file ? pdf.fileName : pdf.fileName || "Select PDF"}
              </label>
              <input
                id={`file-upload-${pdf.id}`}
                type="file"
                className="sr-only" // Tailwind class to hide the default input but keep it accessible
                accept="application/pdf"
                onChange={(e) => handleFileSelect(index, e)}
                disabled={pdf.loading}
              />
            </div>
            {pdf.fileName && !pdf.loading && !pdf.status.message && (
              <div className="flex-1 min-w-0 overflow-hidden">
                {" "}
                {/* New wrapper div for better truncation */}
                <span className="text-sm text-gray-500 truncate whitespace-nowrap hidden md:block">
                  Current:
                  {pdf.fullPath && (
                    <a
                      href={pdf.fullPath}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="ml-2 text-indigo-600 hover:underline"
                    >
                      (View)
                    </a>
                  )}
                </span>
              </div>
            )}

            <button
              onClick={() => handleUpdate(index)}
              disabled={pdf.loading || !pdf.file}
              className="w-full sm:w-auto px-5 py-2 bg-indigo-600 text-white text-sm font-medium rounded-md hover:bg-indigo-700 transition-colors disabled:opacity-60 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 flex-shrink-0" /* Added flex-shrink-0 */
            >
              {pdf.loading ? (
                <div className="flex items-center justify-center">
                  <svg
                    className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Updating...
                </div>
              ) : (
                "Update PDF"
              )}
            </button>
          </div>
        </div>
      ))}
    </div>
  );
};

export default UploadPdfs;
